import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import TableComponent from "../../components/TableComponent";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { LoanEquipmentSearchResultDTO } from "../../models/LoanEquipment/LoanEquipmentSearchResultDTO";
import { Parameter } from "../../models/Parameter";
import { LoanEquipmentService } from "../../services/LoanEquipmentService";
import RoutesObject from "../../helpers/Routes";
import Layout from "../../components/Layout/Layout";
import Routes from "../../helpers/Routes";

export default function LoanEquipmentIndex() {
    const intl = useIntl();
    const navigate = useNavigate();
    const [name, setName] = useState<string>(
        Utilities.LoadParameterFromURLQuery("Name", "string", ""),
    );
    const [deliveryDate, setDeliveryDate] = useState<string>(
        Utilities.LoadParameterFromURLQuery("DeliveryDate", "string", ""),
    );
    const [pickupDate, setPickupDate] = useState<string>(
        Utilities.LoadParameterFromURLQuery("PickupDate", "string", ""),
    );
    const [data, setData] = useState<LoanEquipmentSearchResultDTO[]>([]);
    const loanEquipmentService = new LoanEquipmentService();
    const [initialSortBy] = useState<SortingRule<object>[]>(
        Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"),
    );
    const pageSizes: number[] = [5, 15, 30];
    const [goToPage, setGoToPage] = useState<number>();
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes));
    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);
    const [searchParams] = useSearchParams();

    const onEnter = () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };    
      const columns = React.useMemo(
        () => [
            {
                Header: intl.formatMessage({ id: "LoanEquipment.Field.Name" }),
                accessor: "name",
                width: 40
            },
            {
                Header: intl.formatMessage({ id: "LoanEquipment.Field.ClientName" }),
                accessor: "clientName",
                width: 40
            },
            {
                Header: intl.formatMessage({ id: "LoanEquipment.Field.DeliveryDate" }),
                accessor: "dateDeliveredLoanEquipment",
                width: 5,
                Cell: (props: any) => {
                    return <span>{props.value ? new Date(props.value).toLocaleDateString() : "-"}</span>;
                },
            },
            {
                Header: intl.formatMessage({ id: "LoanEquipment.Field.PickupDate" }),
                accessor: "datePickedupLoanEquipment",
                width: 5,
                Cell: (props: any) => {
                    return <span>{props.value ? new Date(props.value).toLocaleDateString() : "-"}</span>;
                },
            },
            {
                Header: "",
                accessor: "actions",
                width: 10,
                disableSortBy: true,
                Cell: (props: any) => {
                    return (
                        <div>
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Button.ViewAssistance" })}
                                onClick={() => navigate(`${Routes.backoffice.details}/${props.row.original.assistanceId}`)}
                            />
                        </div>
                    );
                },
            },
        ],
        [intl, navigate]
    );
      const fetchData = async (page: number, pageSize: number, sortBy: SortingRule<object>[] | null) => {
        var searchUrl: string = Utilities.CreateSearchURL([
            `page=${page}`,
            `pageSize=${pageSize}`,
            Utilities.AddParamToQueryIfValue("Name", "string", name),
            Utilities.AddParamToQueryIfValue("DeliveryDate", "string", deliveryDate),
            Utilities.AddParamToQueryIfValue("PickupDate", "string", pickupDate),
        ]);

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue("sortField", "string", sortBy[0].id)}${Utilities.AddParamToQueryIfValue(
                "sortOrder", "string", sortBy[0].desc === true ? "DESC" : "ASC",
            )}`;
        }

        if (searchParams.toString() === "") {
            navigate(`${RoutesObject.loanEquipments.list}${searchUrl}`, { replace: true });
        } else {
            navigate(`${RoutesObject.loanEquipments.list}${searchUrl}`);
        }

        setLoading(true);

        var response = await loanEquipmentService.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize,
        );

        if (response.success !== true) {
            setCurrentPage(1);
            setGoToPage(1);
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        if (response.items.length === 0) {
            setCurrentPage(1);
            setGoToPage(1);
        }

        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0);

        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];

        parameters.push(new Parameter("name", name));
        parameters.push(new Parameter("deliveryDate", deliveryDate));
        parameters.push(new Parameter("pickupDate", pickupDate));
        return parameters;
    };

    const onRemoveFiltersClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        setName("");
        setDeliveryDate("");
        setPickupDate("");
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);

    useEffect(() => {
        fetchData(page, pageSize, initialSortBy);
    }, []);

    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <span className="pageTitle">
                            <FormattedMessage id="Screens.LoanEquipments.Title" />
                        </span>
                    </div>
                    <div className="pageBody">

                        <Row>
                            <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={name}
                                label={intl.formatMessage({ id: "LoanEquipment.Field.Name" })}
                                readOnly={false}
                                onChange={(t: string) => setName(t)}
                            />
                            <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                                style={{ width: "100%" }}
                                type={"date"}
                                onEnter={onEnter}
                                value={deliveryDate}
                                label={intl.formatMessage({ id: "LoanEquipment.Field.DeliveryDate" })}
                                readOnly={false}
                                onChange={(t: string) => setDeliveryDate(t)}
                            />
                            <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                                style={{ width: "100%" }}
                                type={"date"}
                                onEnter={onEnter}
                                value={pickupDate}
                                label={intl.formatMessage({ id: "LoanEquipment.Field.PickupDate" })}
                                readOnly={false}
                                onChange={(t: string) => setPickupDate(t)}
                            />

                        </Row>

                        <Row>
                            <Col xl={6} lg={6} md={6} sm={12} xs={12}
                                style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                    onClick={onButtonClick}
                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                    onClick={onRemoveFiltersClick}
                                />
                            </Col>
                        </Row>
                        <div className="pageComponent" style={{ width: "100%" }}>
                            <TableComponent
                                goToPage={goToPage}
                                loading={loading}
                                pageCount={pageCount}
                                columns={columns}
                                manualPagination={true}
                                fetchData={fetchData}
                                pageSizes={pageSizes}
                                initialPage={page}
                                initialPageSize={pageSize}
                                initialSortBy={initialSortBy}
                                data={data}
                            />
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    )
}
